<template>
  <el-dialog
    :title="$t('addOwnerRepair.addTitle')"
    :visible.sync="visible"
    width="650px"
    custom-class="repair-design-dialog owner-repair-dialog"
    @close="closeDialog"
  >
    <el-form ref="form" :model="formData" :rules="rules" label-width="86px" class="repair-design-form">
      <el-form-item :label="$t('addOwnerRepair.community')" prop="communityId">
        <el-select v-model="formData.communityId" :placeholder="$t('addOwnerRepair.selectPlaceholder')" @change="handleCommunityChange">
          <el-option v-for="item in communityList" :key="item.communityId" :label="item.name" :value="item.communityId" />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.repairArea')" prop="repairObjType">
        <el-radio-group v-model="formData.repairObjType" class="repair-area-radio" @change="changeRepairObjType">
          <el-radio label="001">{{ $t('addOwnerRepair.publicArea') }}</el-radio>
          <el-radio label="004">{{ $t('addOwnerRepair.room') }}</el-radio>
        </el-radio-group>
      </el-form-item>


      <el-form-item :label="$t('addOwnerRepair.repairType')" prop="repairSettingType">
        <el-select v-model="formData.repairSettingType" :placeholder="$t('addOwnerRepair.selectPlaceholder')" @change="handleRepairSettingTypeChange">
          <el-option :label="$t('repairSetting.cleaningOrder')" value="100" />
          <el-option :label="$t('repairSetting.repairOrder')" value="200" />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.typeName')" prop="repairType">
        <el-select v-model="formData.repairType" :placeholder="$t('addOwnerRepair.selectPlaceholder')">
          <el-option v-for="item in filteredRepairSettings" :key="item.repairType" :label="item.repairTypeName" :value="item.repairType" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="formData.repairObjType === '004'" :label="$t('addOwnerRepair.room')" prop="roomId">
        <div class="room-picker-line">
          <el-input v-model="formData.roomName" :placeholder="$t('addOwnerRepair.selectPlaceholder')" readonly />
          <el-button type="primary" @click="openRoomPicker">{{ $t('addOwnerRepair.selectRoom') }}</el-button>
        </div>
      </el-form-item>

      <el-form-item v-if="formData.repairObjType === '001'" :label="$t('addOwnerRepair.location')" prop="repairObjName">
        <el-input v-model.trim="formData.repairObjName" :placeholder="$t('addOwnerRepair.placeholder.location')" />
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.repairName')" prop="repairName">
        <el-input v-model.trim="formData.repairName" :placeholder="$t('addOwnerRepair.inputPlaceholder')" />
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.tel')" prop="tel">
        <el-input v-model.trim="formData.tel" type="tel" :placeholder="$t('addOwnerRepair.inputPlaceholder')" />
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.appointmentTime')" required>
        <div class="appointment-slot-line">
          <el-form-item prop="appointmentDate">
            <el-date-picker
              v-model="formData.appointmentDate"
              type="date"
              value-format="yyyy-MM-dd"
              :placeholder="$t('addOwnerRepair.placeholder.appointmentDate')"
              @change="handleAppointmentSlotChange"
            />
          </el-form-item>
          <el-form-item prop="appointmentSlot">
            <el-select v-model="formData.appointmentSlot" :placeholder="$t('addOwnerRepair.placeholder.appointmentSlot')" @change="handleAppointmentSlotChange">
              <el-option v-for="slot in appointmentSlots" :key="slot.value" :label="slot.label" :value="slot.value" />
            </el-select>
          </el-form-item>
        </div>
      </el-form-item>

      <el-form-item :label="$t('addOwnerRepair.context')" prop="context">
        <el-input v-model.trim="formData.context" :placeholder="$t('addOwnerRepair.inputPlaceholder')" />
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="visible = false">{{ $t('common.cancel') }}</el-button>
      <el-button type="primary" @click="saveOwnerRepairInfo">{{ $t('common.confirm') }}</el-button>
    </span>

    <search-room ref="searchRoom" :community-id="formData.communityId" @chooseRoom="handleChooseRoom" />
  </el-dialog>
</template>

<script>
import * as ownerRepairManageApi from '@/api/work/ownerRepairManageApi'
import { getCommunityName } from '@/api/community/communityApi'
import { listCommunitys } from '@/api/community/communityManageApi'
import SearchRoom from '@/components/room/searchRoom'

function normalizeCommunityId(communityId) {
  return communityId === '-1' || communityId === -1 ? '' : communityId
}

export default {
  name: 'AddOwnerRepair',
  components: {
    SearchRoom
  },
  data() {
    return {
      visible: false,
      communityName: getCommunityName(),
      communityList: [],
      formData: this.getDefaultFormData(),
      repairSettings: [],
      appointmentSlots: Array.from({ length: 12 }, (_, index) => {
        const startHour = index * 2
        const endHour = startHour + 2
        return {
          value: String(startHour),
          label: `${String(startHour).padStart(2, '0')}:00-${endHour === 24 ? '24:00' : `${String(endHour).padStart(2, '0')}:00`}`
        }
      }),
      rules: {
        communityId: [{ required: true, message: this.$t('addOwnerRepair.placeholder.community'), trigger: 'change' }],
        repairObjType: [{ required: true, message: this.$t('addOwnerRepair.placeholder.repairArea'), trigger: 'change' }],
        roomId: [{ required: true, message: this.$t('addOwnerRepair.placeholder.room'), trigger: 'change' }],
        repairObjName: [{ required: true, message: this.$t('addOwnerRepair.placeholder.location'), trigger: 'blur' }],
        repairSettingType: [{ required: true, message: this.$t('addOwnerRepair.placeholder.repairType'), trigger: 'change' }],
        repairType: [{ required: true, message: this.$t('addOwnerRepair.placeholder.typeName'), trigger: 'change' }],
        repairName: [{ required: true, message: this.$t('addOwnerRepair.placeholder.repairName'), trigger: 'blur' }],
        tel: [
          { required: true, message: this.$t('addOwnerRepair.placeholder.tel'), trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: this.$t('repairPoolManage.contactFormat'), trigger: 'blur' }
        ],
        appointmentDate: [{ required: true, message: this.$t('addOwnerRepair.placeholder.appointmentDate'), trigger: 'change' }],
        appointmentSlot: [{ required: true, message: this.$t('addOwnerRepair.placeholder.appointmentSlot'), trigger: 'change' }],
        context: [{ required: true, message: this.$t('addOwnerRepair.placeholder.context'), trigger: 'blur' }]
      }
    }
  },
  computed: {
    filteredRepairSettings() {
      if (!this.formData.repairSettingType) {
        return this.repairSettings
      }
      return this.repairSettings.filter(item => String(item.repairSettingType || '') === String(this.formData.repairSettingType))
    }
  },
  methods: {
    getDefaultFormData() {
      const currentCommunityId = normalizeCommunityId(this.getCommunityId())
      return {
        communityId: currentCommunityId,
        repairType: '',
        repairSettingType: '',
        repairName: '',
        tel: '',
        appointmentTime: '',
        appointmentStartTime: '',
        appointmentEndTime: '',
        appointmentDate: '',
        appointmentSlot: '',
        context: '',
        repairObjType: '001',
        repairObjId: currentCommunityId,
        repairObjName: '',
        repairChannel: 'T',
        roomId: '',
        roomName: ''
      }
    },

    async open() {
      this.communityName = getCommunityName()
      await this.loadCommunityList()
      this.formData = this.getDefaultFormData()
      if (this.formData.communityId) {
        this.formData.repairObjId = this.formData.communityId
      }
      this.visible = true
      this.listRepairSettings()
    },

    closeDialog() {
      this.visible = false
      this.resetForm()
    },

    resetForm() {
      this.$refs.form && this.$refs.form.resetFields()
      this.formData = this.getDefaultFormData()
    },

    async loadCommunityList() {
      try {
        const res = await listCommunitys({ page: 1, row: 1000 })
        const communities = res.communitys || res.data || []
        this.communityList = communities.map(item => ({
          ...item,
          communityId: String(item.communityId)
        })).filter(item => item.communityId && item.communityId !== '-1')
      } catch (error) {
        console.error('加载小区列表失败:', error)
        this.communityList = []
      }
    },

    getSelectedCommunityName(communityId) {
      const current = (this.communityList || []).find(item => String(item.communityId) === String(communityId))
      return current ? current.name : getCommunityName()
    },

    handleCommunityChange(communityId) {
      const currentCommunityId = normalizeCommunityId(communityId)
      this.communityName = this.getSelectedCommunityName(communityId)
      this.formData.repairType = ''
      this.formData.roomId = ''
      this.formData.roomName = ''
      if (this.formData.repairObjType === '001') {
        this.formData.repairObjId = currentCommunityId
      } else {
        this.formData.repairObjId = ''
        this.formData.repairObjName = ''
      }
      this.listRepairSettings()
    },

    handleChooseRoom(room) {
      this.formData.roomId = room.roomId
      this.formData.repairObjId = room.roomId
      this.formData.roomName = this.formatRoomName(room)
      this.formData.repairObjName = this.formData.roomName
      this.formData.repairName = room.ownerName || this.formData.repairName
      this.formData.tel = room.link || this.formData.tel
    },

    openRoomPicker() {
      if (!this.formData.communityId) {
        return
      }
      this.$refs.searchRoom.open(this.formData.communityId)
    },

    formatRoomName(room) {
      return room.roomName || [room.floorNum, room.unitNum, room.roomNum].filter(Boolean).join('-')
    },

    changeRepairObjType() {
      this.formData.roomId = ''
      this.formData.roomName = ''
      if (this.formData.repairObjType === '001') {
        this.formData.repairObjId = this.formData.communityId
      } else {
        this.formData.repairObjId = ''
        this.formData.repairObjName = ''
      }
    },

    handleRepairSettingTypeChange() {
      this.formData.repairType = ''
    },

    handleAppointmentSlotChange() {
      if (!this.formData.appointmentDate || this.formData.appointmentSlot === '') {
        this.formData.appointmentStartTime = ''
        this.formData.appointmentEndTime = ''
        this.formData.appointmentTime = ''
        return
      }
      const startHour = Number(this.formData.appointmentSlot)
      const endHour = startHour + 2
      this.formData.appointmentStartTime = `${this.formData.appointmentDate} ${this.formatHour(startHour)}:00:00`
      this.formData.appointmentEndTime = `${this.formData.appointmentDate} ${endHour === 24 ? '23:59:59' : `${this.formatHour(endHour)}:00:00`}`
      this.formData.appointmentTime = this.formData.appointmentStartTime
    },

    formatHour(hour) {
      return String(hour).padStart(2, '0')
    },

    async listRepairSettings() {
      const params = {
        page: 1,
        row: 50,
        communityId: this.formData.communityId
      }
      const { data } = await ownerRepairManageApi.listRepairSettings(params)
      this.repairSettings = data || []
    },

    saveOwnerRepairInfo() {
      if (this.formData.repairObjType === '001') {
        this.formData.repairObjId = normalizeCommunityId(this.formData.communityId)
      }
      this.formData.communityId = normalizeCommunityId(this.formData.communityId)
      this.handleAppointmentSlotChange()

      this.$refs.form.validate(valid => {
        if (valid) {
          ownerRepairManageApi.saveOwnerRepair(this.formData).then(res => {
            if (res.code === 0) {
              this.$message.success(this.$t('common.operationSuccess'))
              this.visible = false
              this.$emit('success')
            } else {
              this.$message.error(res.msg)
            }
          }).catch(error => {
            this.$message.error(error.message || this.$t('common.operationFailed'))
          })
        }
      })
    }
  }
}
</script>

<style lang="scss">
.repair-design-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    height: 66px;
    padding: 0 24px;
    background: #ebf2fa;
    border-bottom: 1px solid #dce6f5;
    line-height: 66px;
  }

  .el-dialog__title {
    color: #1d2129;
    font-size: 16px;
    font-weight: 600;
  }

  .el-dialog__headerbtn {
    top: 23px;
    right: 24px;
  }

  .el-dialog__body {
    padding: 24px 24px 14px;
  }

  .el-dialog__footer {
    padding: 10px 24px 24px;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  .el-button {
    min-width: 98px;
    height: 38px;
    border-radius: 10px;
    font-size: 14px;
  }

  .el-button--default {
    color: #0e76ff;
    border-color: #0e76ff;
  }

  .el-button--primary {
    background: #0e76ff;
    border-color: #0e76ff;
  }
}

.repair-design-form {
  .el-form-item {
    margin-bottom: 18px;
  }

  .el-form-item__label {
    color: #1d2129;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }

  .el-form-item__content {
    line-height: 38px;
  }

  .el-input__inner,
  .el-textarea__inner {
    height: 38px;
    border-color: #dce6f5;
    border-radius: 10px;
    color: #1d2129;
    font-size: 14px;
  }

  .el-select,
  .el-date-editor.el-input {
    width: 100%;
  }

  .repair-area-radio {
    height: 38px;
    display: flex;
    align-items: center;
  }

  .room-picker-line {
    display: flex;
    gap: 12px;

    .el-input {
      flex: 1;
    }

    .el-button {
      min-width: 98px;
      height: 38px;
      border-radius: 10px;
    }
  }

  .appointment-slot-line {
    display: flex;
    gap: 12px;

    .el-form-item {
      flex: 1;
      margin-bottom: 0;
    }
  }
}
</style>
